/**
 * PromoterLeaderboard.tsx - Promoter Rankings and Performance Display
 *
 * This component displays a leaderboard of fundraiser promoters with their
 * performance metrics, rankings, and progress toward goals. Supports highlighting
 * specific promoters and responsive design.
 *
 * Key Features:
 * - Promoter rankings with performance metrics
 * - Highlighted promoter support for specific views
 * - Progress bars and goal tracking
 * - Responsive design with configurable display count
 * - Empty state handling for no promoters
 * - Avatar display with fallback initials
 *
 * Display Options:
 * - Configurable number of promoters to show
 * - Promoter highlighting for specific contexts
 * - Progress visualization with goal percentages
 * - Rank badges and performance indicators
 *
 * Props:
 * - highlightPromoterId?: string - Promoter ID to highlight
 * - showPromoter?: boolean - Whether to show promoter section
 * - promoterId?: string - Specific promoter to focus on
 *
 * Dependencies: MobX store, styled-components, Lucide icons
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { TrendingUp, Users } from "lucide-react";
import { PromoterProgressModel } from "../models";
import { fundraiserStore } from "../stores";

const LeaderboardContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RankBadge = styled.div`
  background: #10b981;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 6px;
`;

const PromoterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PromoterItem = styled.div<{ isCurrentUser?: boolean }>`
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  background: ${(props) => (props.isCurrentUser ? "#f0fdf4" : "#f8fafc")};
  border: ${(props) =>
    props.isCurrentUser ? "2px solid #10b981" : "1px solid #e2e8f0"};
  transition: all 0.2s ease;
  opacity: 0;
  animation: promoterItemSlideIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;

  &:nth-child(1) {
    animation-delay: 0.1s;
  }
  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
  &:nth-child(4) {
    animation-delay: 0.4s;
  }
  &:nth-child(5) {
    animation-delay: 0.5s;
  }

  @keyframes promoterItemSlideIn {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const PromoterInfo = styled.div`
  flex: 1;
`;

const PromoterName = styled.div<{ isCurrentUser?: boolean }>`
  font-size: 14px;
  font-weight: ${(props) => (props.isCurrentUser ? "700" : "600")};
  color: ${(props) => (props.isCurrentUser ? "#059669" : "#1e293b")};
  margin-bottom: 2px;
`;

const DonationCount = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const DonorStats = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const Amount = styled.div<{ isCurrentUser?: boolean }>`
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => (props.isCurrentUser ? "#059669" : "#10b981")};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  min-height: 200px;
`;

const EmptyStateIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #94a3b8;
`;

const EmptyStateTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

interface PromoterLeaderboardProps {
  highlightPromoterId?: string;
  leaderboard?: PromoterProgressModel[];
}

export const PromoterLeaderboard: React.FC<PromoterLeaderboardProps> = observer(
  ({ highlightPromoterId, leaderboard }) => {
    //const fundraiserId = fundraiserStore.selectedFundraiserId || '1';
    //const promoters = fundraiserStore.getFundraiserPromoters(fundraiserId);

    // Use live builder data when available, otherwise use defaults
    const selectedFundraiser = fundraiserStore.selectedFundraiser;
    const builderData = fundraiserStore.builderData;
    const hasLiveBuilderData =
      builderData.id &&
      selectedFundraiser &&
      builderData.id === selectedFundraiser.id;

    const promoterCount = hasLiveBuilderData
      ? builderData.promoterCount || 5
      : 5;

    //const donations = fundraiserStore.getFundraiserDonations(fundraiserId);
    const hasDonations = leaderboard && leaderboard.length > 0;

    // Check if this is a promoter-specific view by looking at URL or passed highlightPromoterId
    const storedHighlightPromoterId = sessionStorage.getItem(
      "highlightPromoterId"
    );
    const actualHighlightPromoterId =
      highlightPromoterId || storedHighlightPromoterId;
    const isPromoterView = !!actualHighlightPromoterId;

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const topPromoters = leaderboard ? leaderboard.slice(0, promoterCount) : [];

    return (
      <LeaderboardContainer>
        <Header>
          <Title>
            <TrendingUp size={20} className="text-green-500" />
            Leaderboard
          </Title>
          {isPromoterView && <RankBadge>#3</RankBadge>}
        </Header>

        {!hasDonations ? (
          <EmptyState>
            <EmptyStateIcon>
              <Users size={24} />
            </EmptyStateIcon>
            <EmptyStateTitle>No Leaders Yet</EmptyStateTitle>
            <EmptyStateText>
              Promoter rankings will appear here once donations are received.
            </EmptyStateText>
          </EmptyState>
        ) : (
          <PromoterList>
            {topPromoters.map((promoter) => {
              const isCurrentUser =
                actualHighlightPromoterId &&
                promoter.id === actualHighlightPromoterId
                  ? true
                  : false;
              const hasGoal = promoter.goalAmount && promoter.goalAmount > 0;
              const donationCount = Math.floor(Math.random() * 50) + 10; // Mock donation count

              return (
                <PromoterItem key={promoter.id} isCurrentUser={isCurrentUser}>
                  {promoter.imageURL && (
                    <Avatar
                      src={promoter.imageURL}
                      alt={promoter.displayName}
                      // onError={(e) => {
                      //   e.currentTarget.src = `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face`;
                      // }}
                    />
                  )}

                  <PromoterInfo>
                    <PromoterName isCurrentUser={isCurrentUser}>
                      {promoter.displayName.replace(" (You)", "")}
                    </PromoterName>
                    {promoter.goalAmount > 0 ? (
                      <DonorStats>
                        {(
                          (promoter.totalRaisedAmount / promoter.goalAmount) *
                          100
                        ).toFixed(1)}
                        % of goal
                      </DonorStats>
                    ) : (
                      <DonorStats>No goal set</DonorStats>
                    )}
                  </PromoterInfo>

                  <Amount isCurrentUser={isCurrentUser}>
                    {formatCurrency(promoter.totalRaisedAmount)}
                  </Amount>
                </PromoterItem>
              );
            })}
          </PromoterList>
        )}
      </LeaderboardContainer>
    );
  }
);
